import React from "react";
import { Link } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import data from "../data";
import {
  DocumentTextIcon,
  CogIcon,
  SparklesIcon,
  ChartBarIcon,
  ShareIcon,
  CurrencyDollarIcon,
  TruckIcon,
  ShoppingBagIcon,
  AcademicCapIcon,
  CpuChipIcon,
  HomeIcon,
  HeartIcon,
} from "@heroicons/react/24/solid";
import SectionLayout from "../components/home/SectionLayout";
import ToolInfo from "../components/home/ToolInfo";
import YouTubeVideoWithCTA from "../components/YoutubeVideoWithCTA";
import CallToActionSection from "../components/CallToActionSection";

const Home = () => {
  return (
    <>
      {/* Hero Section */}
      <div className="relative w-full h-[80vh] bg-black flex items-center align-left">
        <video
          src={data.home.heroVideo}
          autoPlay
          loop
          muted
          className="absolute inset-0 w-full h-full object-cover opacity-50"
        />
        <div className="relative z-10 text-left pl-12 md:pl-24 md:ml-24">
          <h1 className="text-6xl font-bold uppercase text-white leading-tight">
            {data.home.title}
          </h1>
          <p className="mt-2 text-2xl font-light text-gray-200 leading-relaxed">
            {data.home.description}
          </p>
          <div className="mt-8 flex gap-6">
            {/* Primary Button */}
            <Link
              to="/contact?type=Print%20Job"
              className="bg-primary text-white px-8 py-4 text-xl rounded-lg shadow-md transition-transform duration-300 ease-in-out  hover:font-bold"
            >
              Request a Print
            </Link>

            {/* Secondary Button */}
            <Link
              to="/contact?type=Machine%20Inquiry"
              className="bg-secondary text-white px-8 py-4 text-xl rounded-lg shadow-md transition-transform duration-300 ease-in-out  hover:font-bold"
            >
              Request a Demo
            </Link>
          </div>
        </div>
      </div>

      {/* 1. The Inspiration */}
      <SectionLayout
        title="Inspiration"
        description="
    Break free from design limits and create captivating spaces. From bold statements to intricate artwork, vertical printing lets your creativity shine on any surface.
"
        content={
          <>
            <h3 className="text-4xl font-bold text-primary">
              {data.home.whatIsVerticalPrinting.title}
            </h3>
            <p className="text-lg text-gray-700 my-4">
              {data.home.whatIsVerticalPrinting.description}
            </p>
            <div className="">
              <Swiper
                modules={[Navigation, Pagination, Autoplay]}
                spaceBetween={24} // Slightly increased spacing between slides
                slidesPerView={3}
                loop
                autoplay={{ delay: 3000 }}
                navigation
                pagination={{ clickable: true }}
                className="w-full max-w-6xl mx-auto" // Adjusted max width for larger slider
              >
                {data.home.carouselImages.map((image, index) => (
                  <SwiperSlide key={index}>
                    <img
                      src={image}
                      alt={`Printing Example ${index + 1}`}
                      className="w-full h-[250px] object-cover rounded-lg shadow-lg" // Increased image height
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
              <div className="mt-12 text-center">
                <Link
                  to="/print"
                  className="bg-primary text-white px-8 py-4 text-xl rounded-lg shadow-md transition-transform duration-300 ease-in-out  hover:font-bold "
                >
                  View Our Gallery
                </Link>
              </div>
            </div>
          </>
        }
      />

      {/* 2. The Tools */}
      <SectionLayout
        title="Product"
        description="Empower your creativity with the wallPen® E2—the ultimate tool for vertical printing. Engineered for precision and built for efficiency, this state-of-the-art machine transforms how you bring your designs to life. From its seamless setup to its innovative features like UV-cured ink and variable height adjustments, the wallPen® E2 is designed to deliver flawless results on any surface. Whether you're printing on glass, wood, or walls, this tool ensures your work stands out with crisp, vibrant detail and unparalleled ease."
        ctaText="Learn More"
        ctaLink="/machine"
        content={
          <>
            {/* Main Tools Section */}
            <div className="flex flex-col md:flex-row items-center gap-8">
              {/* Left: Image */}
              <div className="md:w-1/3 flex justify-center md:mb-8">
                <img
                  src={data.home.wallPenImage}
                  alt="wallPen Machine"
                  className="w-[150px] h-[400px] object-cover"
                />
              </div>

              {/* Right: Text Content */}
              <div className="md:w-2/3">
                <h3 className="text-3xl font-bold text-primary">
                  The wallPen® E2 Pro
                </h3>
                <ul className="text-md text-gray-700 mt-4 space-y-3">
                  {data.home.features.map((feature, index) => (
                    <li key={index}>• {feature}</li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Additional Features */}
            <ToolInfo />
          </>
        }
        reverse
        background="bg-gray-100"
      />

      {/* 3. The Training */}
      <SectionLayout
        title={data.home.training.title}
        description={data.home.training.description}
        content={
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {data.home.training.businessSupport.map((support, index) => {
                // Map the icons
                const IconComponent = {
                  DocumentTextIcon,
                  CogIcon,
                  SparklesIcon,
                  ChartBarIcon,
                  ShareIcon,
                  CurrencyDollarIcon,
                }[support.icon];

                return (
                  <div
                    key={index}
                    className="flex flex-col items-center text-center space-y-4"
                  >
                    {/* Logo */}
                    <IconComponent className="w-16 h-16 text-secondary" />

                    {/* Title */}
                    <h3 className="text-xl font-bold text-primary uppercase">
                      {support.title}
                    </h3>

                    {/* Description */}
                    <p className="text-gray-700 text-md">
                      {support.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </>
        }
      />

      {/* 4. The Partner */}
      <SectionLayout
        title={data.home.partner.title}
        description={data.home.partner.description}
        reverse
        background="bg-gray-100"
        content={
          <div className="relative">
            {/* Vertical Line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gray-300"></div>

            {/* Build Your Portfolio */}
            <div className="flex items-center mb-16">
              {/* Content */}
              <div className="w-1/2 pr-8 text-right">
                <h3 className="text-2xl font-bold text-primary mb-4">
                  Build Your Portfolio
                </h3>
                <p className="text-gray-700">
                  Start your wall printing business in a growing market full of
                  opportunity. Showcase your talent with a portfolio of
                  stunning, custom designs that captivate clients and build
                  lasting trust. With the wallPen’s unmatched capabilities and
                  our ongoing support, you’ll confidently create a reputation
                  for excellence and watch your business thrive.
                </p>
              </div>
            </div>

            {/* Grow Your Business */}
            <div className="flex items-center mb-16">
              {/* Connector Dot */}
              <div className="w-1/2 flex justify-center">
                <div className=" bg-primary rounded-full border-4 border-white"></div>
              </div>
              {/* Content */}
              <div className="w-1/2 pl-8 text-left">
                <h3 className="text-2xl font-bold text-primary mb-4">
                  Grow Your Business
                </h3>
                <p className="text-gray-700 mb-6">
                  From creating a professional website to acquiring clients and
                  managing your finances, we provide the tools and guidance you
                  need to succeed. Our comprehensive support ensures you’re
                  prepared to run your business smoothly and scale it
                  effectively.
                </p>
                <Link
                  to="/contact"
                  className="bg-primary text-white px-8 py-4 text-xl rounded-lg shadow-md transition-transform duration-300 ease-in-out  hover:font-bold "
                >
                  Contact Us
                </Link>
              </div>
            </div>

            {/* Compete Locally */}
            <div className="flex items-center">
              {/* Content */}
              <div className="w-1/2 pr-8 text-right">
                <h3 className="text-2xl font-bold text-primary mb-4 ">
                  Compete at Locations Near You
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-6">
                  {data.home.partner.notablePrints.map((location, index) => {
                    const IconComponent = {
                      TruckIcon,
                      ShoppingBagIcon,
                      AcademicCapIcon,
                      CpuChipIcon,
                      HomeIcon,
                      HeartIcon,
                    }[location.icon];

                    return (
                      <div
                        key={index}
                        className="flex flex-col items-center text-center space-y-2"
                      >
                        <IconComponent className="w-10 h-10 text-secondary" />
                        <p className="text-primary text-sm font-bold">
                          {location.name}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        }
      />

      {/* Process Timeline */}
      <section className="container mx-auto px-6 py-16">
        <div className="flex flex-col md:flex-row items-start gap-12 px-6 md:px-12">
          {/* Title and Description */}
          <div className="w-full md:w-[40%] text-left">
            <div className="inline-block">
              <h2 className="text-6xl font-bold uppercase text-primary leading-tight mb-2">
                Our Process
              </h2>
              {/* Dynamic Underline matching title width */}
              <div className="border-t-2 border-primary w-full mb-6"></div>
            </div>
            <p className="text-lg text-gray-700">
              Starting your wall printing journey has never been easier. At
              SurfacePix, we simplify every step of the process—from purchasing
              your machine to training, branding, and launching your business.
              Our streamlined approach ensures you’re set up for success,
              whether you’re creating stunning prints or building a client base.
              Let us guide you from the first step to a thriving, sustainable
              business.
            </p>
          </div>

          {/* Process Video */}
          <div className="w-full md:w-[60%]">
            <div className="w-full h-[400px] md:h-[500px] rounded-lg shadow-lg overflow-hidden">
              <YouTubeVideoWithCTA
                videoId={new URL(data.home.processVideo).searchParams.get("v")}
                ctaLink="/contact"
                ctaText="Ready to Start Your Journey?"
              />
            </div>
          </div>
        </div>

        {/* Steps Section - Same Padding */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6 mt-12 md:mt-16 px-6 md:px-12 text-center">
          {data.home.processSteps.map((step, index) => (
            <div key={index} className="flex flex-col items-center">
              <div
                className={`text-4xl font-bold rounded-full p-5 ${
                  index % 2 === 0
                    ? "bg-primary text-white"
                    : "bg-secondary text-black"
                }`}
              >
                {index + 1}
              </div>
              <h3 className="text-xl font-semibold mt-4 text-primary">
                {step.title}
              </h3>
              <p className="text-gray-600 mt-2">{step.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="bg-gray-100 py-16">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-bold uppercase text-primary text-center mb-12">
            See What Others Are Saying…
          </h2>

          {/* Conditional Layout Based on Number of Testimonials */}
          <div
            className={`grid gap-8 ${
              data.home.testimonials.length === 1
                ? "grid-cols-1 max-w-2xl mx-auto"
                : "grid-cols-1 md:grid-cols-2"
            }`}
          >
            {data.home.testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="p-6 bg-white rounded-lg shadow-lg flex flex-col space-y-4"
              >
                {/* Quote */}
                <div className="text-lg text-gray-700 space-y-4">
                  {testimonial.quote.split("\n\n").map((paragraph, i) => (
                    <p key={i}>{paragraph}</p>
                  ))}
                </div>

                {/* Author Details */}
                <div className="flex items-center space-x-4">
                  {/* Name and Company */}
                  <div>
                    <h3 className="font-bold text-primary text-xl">
                      {testimonial.name}
                    </h3>
                    <p className="text-gray-600">{testimonial.company}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CallToActionSection
        title="Ready to Start Your Vertical Printing Business?"
        primaryAction={{
          text: "Request a Demo",
          link: "/contact?type=Machine%20Inquiry",
        }}
        secondaryAction={{
          text: "Request a Print Job",
          link: "/contact?type=Print%20Job",
        }}
      />
    </>
  );
};

export default Home;
